import { Loader, Text, icons } from '@lifebit-ai/lds'
import React from 'react'

import { useImportRequestScanDetails } from 'modules/resultsV2/hooks/useImportRequestScanDetails'
import { ImportRequestScanResult } from 'services/import-requests/enums'
import type { ImportRequestObjectCounts } from 'services/import-requests/types'

import * as styles from './ImportRequestScanDetails.styles'

export const IMPORT_REQUEST_SCAN_COUNTS_TEST_ID = 'import-request-scan-counts'

export interface ImportRequestScanDetailsProps {
  scanResult?: ImportRequestScanResult
  scanStartedAt?: string
  scanCompletedAt?: string
  scanStatusCode?: string
  counts?: ImportRequestObjectCounts
  formattedTotalSize?: string
  isLoading?: boolean
}

export const ScanResultIcon = ({ scanResult }: { scanResult: ImportRequestScanResult }): JSX.Element | null => {
  switch (scanResult) {
    case ImportRequestScanResult.InProgress:
      return <Loader size="small" text={false} data-testid="import-request-scan-in-progress-icon" />
    case ImportRequestScanResult.NotStarted:
      return <icons.WarningSmall data-testid="import-request-scan-not-started-icon" />
    case ImportRequestScanResult.Clean:
      return <icons.CheckInCircleSmall data-testid="import-request-scan-clean-icon" />
    case ImportRequestScanResult.ThreatDetected:
      return <icons.CloseInCircleSmall data-testid="import-request-scan-threat-icon" />
    case ImportRequestScanResult.ScanFailed:
      return <icons.WarningSmall data-testid="import-request-scan-failed-icon" />
    default:
      return null
  }
}

const ScanCountsMetadata = ({
  counts,
  formattedTotalSize
}: {
  counts: ImportRequestObjectCounts
  formattedTotalSize?: string
}) => (
  <>
    <Text variant="small" color="secondary">
      Objects
    </Text>
    <Text variant="small" weight="medium">
      {counts.total.toLocaleString()}
    </Text>
    <Text variant="small" color="secondary">
      Clean
    </Text>
    <styles.ToneValue $tone="success">{counts.clean.toLocaleString()}</styles.ToneValue>
    <Text variant="small" color="secondary">
      Threats found
    </Text>
    <styles.ToneValue $tone={counts.threatDetected === 0 ? 'success' : 'danger'}>
      {counts.threatDetected.toLocaleString()}
    </styles.ToneValue>
    <Text variant="small" color="secondary">
      Unsupported
    </Text>
    <Text variant="small" weight="medium">
      {counts.unsupported.toLocaleString()}
    </Text>
    {formattedTotalSize !== undefined && formattedTotalSize !== '' ? (
      <>
        <Text variant="small" color="secondary">
          Total size
        </Text>
        <Text variant="small" weight="medium">
          {formattedTotalSize}
        </Text>
      </>
    ) : null}
  </>
)

const ImportRequestScanDetails = ({
  scanResult,
  scanStartedAt,
  scanCompletedAt,
  isLoading,
  scanStatusCode,
  counts,
  formattedTotalSize
}: ImportRequestScanDetailsProps) => {
  const {
    title,
    resolvedScanResult,
    scanResultMessage,
    formattedScanStartedAt,
    formattedScanCompletedAt,
    formattedScanStatusCode,
    shouldShowScanMetadata,
    shouldShowScanCounts,
    shouldShowTotalSize,
    scanCounts,
    formattedTotalSize: resolvedFormattedTotalSize
  } = useImportRequestScanDetails({
    scanResult,
    scanStartedAt,
    scanCompletedAt,
    scanStatusCode,
    counts,
    formattedTotalSize,
    isLoading
  })

  const shouldShowDetailsGrid = shouldShowScanMetadata || shouldShowScanCounts || shouldShowTotalSize

  return (
    <styles.ImportRequestScanDetailsContainer>
      <styles.ScanHeaderRow data-testid="import-request-scan-details">
        <styles.IconWrapper $scanResult={resolvedScanResult}>
          <ScanResultIcon scanResult={resolvedScanResult} />
        </styles.IconWrapper>
        <styles.Info>
          <styles.Title>{title}</styles.Title>
          <styles.Message $scanResult={resolvedScanResult}>{scanResultMessage}</styles.Message>
        </styles.Info>
      </styles.ScanHeaderRow>
      {shouldShowDetailsGrid ? (
        <styles.MetadataGrid
          data-testid={shouldShowScanCounts || shouldShowTotalSize ? IMPORT_REQUEST_SCAN_COUNTS_TEST_ID : undefined}
        >
          {shouldShowScanMetadata ? (
            <>
              <Text variant="small" color="secondary">
                Scan start date
              </Text>
              <Text variant="small" weight="medium">
                {formattedScanStartedAt}
              </Text>
              <Text variant="small" color="secondary">
                Scan completion date
              </Text>
              <Text variant="small" weight="medium">
                {formattedScanCompletedAt}
              </Text>
              <Text variant="small" color="secondary">
                Scan status code
              </Text>
              <Text variant="small" weight="medium">
                {formattedScanStatusCode}
              </Text>
            </>
          ) : null}
          {shouldShowScanCounts && scanCounts !== undefined ? (
            <ScanCountsMetadata counts={scanCounts} formattedTotalSize={resolvedFormattedTotalSize} />
          ) : shouldShowTotalSize ? (
            <>
              <Text variant="small" color="secondary">
                Total size
              </Text>
              <Text variant="small" weight="medium">
                {resolvedFormattedTotalSize}
              </Text>
            </>
          ) : null}
        </styles.MetadataGrid>
      ) : null}
    </styles.ImportRequestScanDetailsContainer>
  )
}

export default ImportRequestScanDetails
